/**
 * Builds the physics representation of a level from `LevelData`.
 * The renderer builds its meshes from the same data (rendering/SceneView.ts).
 */
import type * as RAPIER from '@dimforge/rapier3d-compat';
import { rapier } from '../simulation/rapier';
import type { QuatLike } from '../core/Vec3';
import { PhysicsWorld } from '../simulation/PhysicsWorld';
import { eulerDegToQuat, validateLevelData, wedgePoints, type LevelData, type SolidDef } from './LevelData';
import { BUILTIN_MATERIALS, MaterialRegistry } from './SurfaceMaterials';

export interface LoadedLevel {
  data: LevelData;
  materials: MaterialRegistry;
  physics: PhysicsWorld;
}

export class LevelValidationError extends Error {
  constructor(
    levelId: string,
    readonly problems: readonly string[],
  ) {
    super(`Level "${levelId}" is invalid:\n  ${problems.join('\n  ')}`);
    this.name = 'LevelValidationError';
  }
}

function shapeDescFor(solid: SolidDef): RAPIER.ColliderDesc {
  const s = solid.shape;
  if (s.type === 'box') return rapier().ColliderDesc.cuboid(s.halfExtents[0], s.halfExtents[1], s.halfExtents[2]);
  if (s.type === 'cylinder') return rapier().ColliderDesc.cylinder(s.halfHeight, s.radius);
  const desc = rapier().ColliderDesc.convexHull(new Float32Array(wedgePoints(s.halfExtents).flat()));
  if (!desc) throw new Error(`solid ${solid.id}: wedge hull could not be built`);
  return desc;
}

function colliderDescFor(solid: SolidDef, rot: QuatLike): RAPIER.ColliderDesc {
  const [x, y, z] = solid.position;
  return shapeDescFor(solid).setTranslation(x, y, z).setRotation(rot).setFriction(0).setRestitution(0);
}

/** Requires `initPhysics()` to have resolved. Throws LevelValidationError on bad data. */
export function loadLevel(data: LevelData): LoadedLevel {
  const materials = new MaterialRegistry([...BUILTIN_MATERIALS, ...(data.materials ?? [])]);
  const problems = validateLevelData(data, materials);
  if (problems.length > 0) throw new LevelValidationError(data.id, problems);

  const physics = new PhysicsWorld();
  const rot: QuatLike = { x: 0, y: 0, z: 0, w: 1 };
  for (const solid of data.solids) {
    eulerDegToQuat(solid.rotationDeg, rot);
    physics.addCollider(colliderDescFor(solid, rot), materials.get(solid.material), solid.id);
  }
  physics.finalize();
  return { data, materials, physics };
}
